import React, { useState, useEffect } from 'react'
import moment from 'moment'
import styled from 'styled-components'

const formatTime = (seconds) => {
  const days = Math.floor(seconds / (24 * 60 * 60)) // Convert seconds to days
  const remainingMinutes = Math.floor((seconds % (24 * 60 * 60)) / 60) // Remaining minutes

  if (days > 0 && remainingMinutes > 0) {
    return `${days} days and ${remainingMinutes} minutes`
  } else if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`
  } else {
    return `${remainingMinutes} minute${remainingMinutes > 1 ? 's' : ''}`
  }
}

const getNextExecutionInfo = (mode, allowedAt, frequency, window) => {
  if (!mode || !allowedAt || frequency == '0' || !window) return ''

  const now = moment.utc()
  let nextExecution = moment.unix(allowedAt).utc()
  let executionEnded = nextExecution.clone().add(window, 'seconds')

  // If `allowedAt` is in the past, find the next valid execution time
  if (nextExecution.isAfter(now)) {
    const diffSeconds = now.diff(nextExecution, 'seconds')

    if (mode === 'Seconds') {
      const elapsedPeriods = Math.ceil(diffSeconds / frequency) // Rounds up to next period
      nextExecution = nextExecution.add(elapsedPeriods * frequency, 'seconds')
      executionEnded = nextExecution.clone().add(window, 'seconds')
    } else if (mode === 'OnDay') {
      const elapsedMonths = Math.ceil(diffSeconds / (frequency * 30 * 24 * 60 * 60)) // Approximate months
      nextExecution = nextExecution.add(elapsedMonths * frequency, 'months')
      executionEnded = nextExecution.clone().add(window, 'seconds')
    } else if (mode === 'OnLastMonthDay') {
      const elapsedMonths = Math.ceil(diffSeconds / (frequency * 30 * 24 * 60 * 60))
      nextExecution = nextExecution.add(elapsedMonths * frequency, 'months').endOf('month')
      executionEnded = nextExecution.clone().add(window, 'seconds')
    }
  }

  // Format times
  const readableNextExecution = nextExecution.format('ddd, DD MMM YYYY HH:mm:ss [UTC]')
  const readableWindow = formatTime(window)
  const readableFrequency = formatTime(frequency)

  // How long ago the last execution window ended
  const diffSinceEnded = now.diff(executionEnded, 'days')

  // Final check: If after all calculations `nextExecution` is still in the past, return a warning
  if (nextExecution.isBefore(now)) {
    return `⚠️ The last scheduled execution was on ${readableNextExecution}, but the date has passed. The window lasted ${readableWindow} (Ended ${diffSinceEnded} day${diffSinceEnded > 1 ? 's' : ''} ago) and should be repeated every ${readableFrequency}.`
  }

  return `Next execution is on ${readableNextExecution}. The execution window lasts ${readableWindow}, and it repeats every ${readableFrequency}.`
}

// React component to display the timelock information
const TimeLockSummary = ({ task }) => {
  const [executionInfo, setExecutionInfo] = useState('')

  useEffect(() => {
    if (!task?.taskConfig?.timeLock) return
    const { mode, allowedAt, frequency, window } = task.taskConfig.timeLock
    setExecutionInfo(getNextExecutionInfo(mode, allowedAt, frequency, window))
  }, [
    task?.taskConfig?.timeLock?.mode,
    task?.taskConfig?.timeLock?.allowedAt,
    task?.taskConfig?.timeLock?.frequency,
    task?.taskConfig?.timeLock?.window,
  ])

  return <Display>{executionInfo}</Display>
}

export default TimeLockSummary

const Display = styled.div`
  font-size: 12px;
  color: #777;
  padding: 8px 0;
  font-style: italic;
  line-height: 1.5;
`
